import React, { useState, useRef, useEffect } from "react";
import { Sun, Moon, Check } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

export type ThemeMode = "light" | "dark" | "system";

interface ThemeSelectorProps {
  themeMode: ThemeMode;
  onThemeChange: (mode: ThemeMode) => void;
  id?: string;
}

export function HalfCircleIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" stroke="none" />
    </svg>
  );
}

export default function ThemeSelector({
  themeMode,
  onThemeChange,
  id = "theme-selector-dropdown"
}: ThemeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown on click outside or Escape key
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Determine current active trigger icon
  const renderTriggerIcon = () => {
    switch (themeMode) {
      case "light":
        return <Sun className="w-4.5 h-4.5 text-amber-500 dark:text-amber-400" />;
      case "dark":
        return <Moon className="w-4.5 h-4.5 text-slate-700 dark:text-slate-200" />;
      case "system":
      default:
        return <HalfCircleIcon className="w-4.5 h-4.5 text-slate-700 dark:text-slate-200" />;
    }
  };

  const options: { mode: ThemeMode; label: string; icon: React.ReactNode }[] = [
    {
      mode: "light",
      label: "Light",
      icon: <Sun className="w-5 h-5 text-current shrink-0" />
    },
    {
      mode: "dark",
      label: "Dark",
      icon: <Moon className="w-5 h-5 text-current shrink-0" />
    },
    {
      mode: "system",
      label: "System",
      icon: <HalfCircleIcon className="w-5 h-5 text-current shrink-0" />
    }
  ];

  return (
    <div className="relative inline-block text-left" ref={containerRef} id={id}>
      {/* Circular Trigger Button matching the design */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={`Current theme: ${themeMode}. Click to switch.`}
        title={`Current theme: ${themeMode.charAt(0).toUpperCase() + themeMode.slice(1)}`}
        className={`w-[38px] h-[38px] rounded-full flex items-center justify-center border transition-all cursor-pointer shadow-2xs shrink-0 active:scale-95 ${
          isOpen
            ? "bg-slate-200 dark:bg-slate-800 border-slate-300 dark:border-slate-700 ring-2 ring-slate-400/30"
            : "bg-white/95 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-750 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
        }`}
        id="theme-toggle-btn"
      >
        {renderTriggerIcon()}
      </button>

      {/* Floating Theme Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.93, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.93, y: -6 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="absolute right-0 top-full mt-2 w-44 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 p-1.5 shadow-2xl z-50 backdrop-blur-xl focus:outline-hidden"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="theme-toggle-btn"
          >
            <div className="flex flex-col gap-0.5">
              {options.map((opt) => {
                const isSelected = themeMode === opt.mode;
                return (
                  <button
                    key={opt.mode}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      onThemeChange(opt.mode);
                      setIsOpen(false);
                    }}
                    className={`flex items-center justify-between w-full px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors cursor-pointer select-none text-left ${
                      isSelected
                        ? "bg-slate-100/90 dark:bg-white/10 text-slate-900 dark:text-white font-semibold"
                        : "text-slate-700 dark:text-slate-200 hover:bg-slate-100/70 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white"
                    }`}
                  >
                    <div className="flex items-center gap-3.5 min-w-0">
                      <span className={isSelected ? "text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400"}>
                        {opt.icon}
                      </span>
                      <span className="truncate">{opt.label}</span>
                    </div>

                    {isSelected && (
                      <Check className="w-4 h-4 text-slate-900 dark:text-white shrink-0 ml-2" />
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
